import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, http } from '@/api/client'
import { ApiError } from '@/api/errors'
import type { StoredFile } from '@/features/students/types'
import type { ApiItem } from '@/types/api'
import type { HomepageEditorData, HomepagePayload, PublicAdmissions, PublicHomepageResponse } from './types'

/** The homepage of the school at this address ({enabled: false} when it has none). */
export const publicHomepageQuery = () =>
  queryOptions({
    queryKey: ['public-homepage'],
    queryFn: () => api.get<PublicHomepageResponse>('/public/homepage'),
    staleTime: 60_000,
    retry: false,
  })

/**
 * Online admissions of the school at this address (admissions module). Null when the module is
 * off or absent (404), so the page simply leaves admissions out.
 */
export const publicAdmissionsQuery = () =>
  queryOptions({
    queryKey: ['public-admissions'],
    queryFn: async (): Promise<PublicAdmissions | null> => {
      try {
        return await api.get<PublicAdmissions>('/public/admissions')
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null
        throw e
      }
    },
    staleTime: 60_000,
    retry: false,
  })

export const homepageEditorQuery = () =>
  queryOptions({
    queryKey: ['homepage-editor'],
    queryFn: () => api.get<HomepageEditorData>('/school/homepage'),
  })

export function useSaveHomepage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: HomepagePayload) => api.put<HomepageEditorData>('/school/homepage', payload),
    onSuccess: (data) => {
      qc.setQueryData(['homepage-editor'], data)
      void qc.invalidateQueries({ queryKey: ['public-homepage'] })
    },
  })
}

/** Uploads a cover or gallery picture (JPG, PNG or WebP, 5 MB at most). */
export function useUploadHomepageImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const res = await http.post<ApiItem<StoredFile>>('/school/homepage/images', form)
      return res.data.data
    },
  })
}
